import { createApiClient } from './api/client'
import { installCapture } from './capture'
import { resolveStrings } from './widget/i18n'
import { mountWidget } from './widget/mount'
import type { FeedbackApi, FeedbackConfig, ReportPayload, ReportTransformer, UserIdentity } from './types'

export interface InternalConfig extends FeedbackConfig {
  fetchImpl?: typeof fetch
}

interface WindowWithGlobal extends Window {
  mhosaicFeedback?: FeedbackApi
}

/** Default offset for the secondary QA FAB: center the 24px QA dot above the
 *  48px feedback FAB (bottom:24 right:24). → { right: 36, bottom: 84 }. */
function defaultQaPosition(): { right: number; bottom: number } {
  return { right: 24 + (48 - 24) / 2, bottom: 24 + 48 + 12 }
}

export function createFeedback(config: InternalConfig): FeedbackApi & { _registerTransformer(fn: ReportTransformer): void } {
  const env = config.env ?? 'prod'
  const locale =
    config.locale ?? (typeof navigator !== 'undefined' ? navigator.language : undefined)
  const strings = resolveStrings(
    config.translations ?? {},
    locale !== undefined ? { locale } : {},
  )
  const capture = installCapture({
    ...(config.sanitizeUrl !== undefined && { sanitizeUrl: config.sanitizeUrl }),
  })
  let user: UserIdentity | undefined = config.user
  let metadata: Record<string, unknown> = config.metadata ?? {}

  const api = createApiClient({
    apiKey: config.apiKey,
    endpoint: config.endpoint,
    ...(config.fetchImpl !== undefined && { fetch: config.fetchImpl }),
    ...(config.beforeSend !== undefined && { beforeSend: config.beforeSend }),
    // v0.13: the API client reads this on every request so a late
    // `identify({userHash, exp, ...})` lights up signed headers
    // immediately on subsequent calls. Returns null when the host
    // hasn't supplied a signature (legacy unsigned path).
    getSignedIdentity: () => {
      if (!user || !user.userHash || !user.exp || !user.email) return null
      return {
        userHash: user.userHash,
        exp: user.exp,
        email: user.email,
      }
    },
  })
  const transformers: ReportTransformer[] = []

  const host = document.createElement('div')
  host.className = 'mhosaic-feedback'
  if (config.attachTo) {
    const attach = typeof config.attachTo === 'string' ? document.querySelector(config.attachTo) : config.attachTo
    attach?.appendChild(host)
  } else {
    document.body.appendChild(host)
  }

  async function buildAndSubmit(values: {
    description: string
    feedback_type?: string
    severity?: string
    synthetic?: boolean
    /** Screenshot the user explicitly attached — via dropzone, paste, file
     *  picker, or the annotator. v0.12 dropped html2canvas-on-submit;
     *  v0.7.1 dropped getDisplayMedia. If the user didn't attach anything,
     *  the report ships without a screenshot. */
    screenshot?: Blob
    /** Capture-method label for the payload. Always "manual" now (v0.7.1);
     *  the union type stays open against the backend schema, which still
     *  recognizes legacy values on existing reports. */
    capture_method?: 'manual'
  }) {
    // Auto-error reports never carry a screenshot — the captured ring
    // buffer (errors, console, network) carries the signal, and the DOM
    // is typically in an inconsistent state when a JS error fires anyway.
    const manualScreenshot = values.synthetic ? undefined : values.screenshot
    const technical_context = capture.snapshot()
    // Surface identify()/setMetadata() values on the report. Without this
    // the host-supplied user identity and metadata sit in closure forever
    // and never reach the operator — a stack trace from a logged-in user
    // looks anonymous on the dashboard.
    //
    // SECURITY: identify() may carry `userHash` + `exp` (the host's HMAC
    // envelope used to attest the user's identity to the backend). That
    // envelope is a bearer-style auth token until it expires; leaking it
    // into technical_context surfaces it on the operator dashboard and to
    // anyone who can read the report. Strip it before embedding.
    if (user) {
      const { userHash: _hash, exp: _exp, ...safeUser } = user
      technical_context.user = safeUser
    }
    if (metadata && Object.keys(metadata).length > 0) {
      technical_context.metadata = { ...metadata }
    }
    const captureMethod: ReportPayload['capture_method'] = manualScreenshot
      ? (values.capture_method ?? 'manual')
      : 'none'
    const payload: ReportPayload = {
      description: values.description,
      feedback_type: (values.feedback_type ?? 'bug') as ReportPayload['feedback_type'],
      severity: (values.severity ?? 'medium') as ReportPayload['severity'],
      env,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      capture_method: captureMethod,
      technical_context,
    }
    // Build-time stamp; tsup's `define` substitutes the package.json
    // version into __MFB_VERSION__. Conditionally assigned so
    // exactOptionalPropertyTypes stays happy under vitest (no tsup
    // pass) where the constant is undefined.
    if (typeof __MFB_VERSION__ !== 'undefined' && __MFB_VERSION__) {
      payload.widget_version = __MFB_VERSION__
    }
    if (manualScreenshot) payload.screenshot = manualScreenshot
    if (values.synthetic) payload.synthetic = true
    // v0.7: lift the host-provided identity to the top level so the
    // backend can upsert a WidgetUser FK. technical_context.user still
    // carries it for backward-compat with the operator's tech-context
    // viewer (and so an audit-log entry knows who was identified at
    // submission time).
    if (user?.id !== undefined && user.id !== null && user.id !== '') {
      payload.user = {
        // The host can pass `id` as a string or number; the backend
        // stores it as an opaque string. Coerce here to a stable shape.
        id: String(user.id),
        ...(user.email !== undefined && { email: user.email }),
        ...(user.name !== undefined && { name: user.name }),
      }
    }
    let finalPayload: ReportPayload = payload
    for (const t of transformers) finalPayload = await t(finalPayload)
    try {
      const result = await api.submitReport(finalPayload)
      config.onSubmitSuccess?.(result)
      capture.clear()
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      config.onError?.(error)
      throw error
    }
  }

  const handle = mountWidget({
    host,
    strings,
    showFAB: config.showFAB ?? true,
    onSubmit: async (values) => { await buildAndSubmit(values) },
    api,
    // Keep this a callback (not a snapshot) so the mount picks up identity
    // changes that happen after createFeedback() — `notifyIdentityChanged()`
    // is the trigger for a re-render.
    getExternalId: () => (user?.id !== undefined && user.id !== null && user.id !== '' ? String(user.id) : undefined),
    // Phase 4: the manifest tells the loader whether this project gates the
    // widget per end-user; the loader forwards it as `requiresVisibilityCheck`.
    requiresVisibilityCheck: config.requiresVisibilityCheck ?? false,
    // Send the identified email alongside the id so an email-based allowlist
    // can match (the backend matches external_id OR email). Read from `user`
    // live so identity set after createFeedback() is reflected.
    checkVisibility: (externalId) => api.checkVisibility(externalId, user?.email),
  })

  // Opt-in QA Meter second FAB. Lazily imported so the QA bytes stay out of
  // the main bundle for consumers who don't enable it. The host serves its
  // own artifact via qaMeter.source.
  let qaHandle: { dispose(): void } | undefined
  let qaDisposed = false
  if (config.qaMeter?.source) {
    const qa = config.qaMeter
    const qaLocale: 'fr' | 'en' =
      qa.locale ?? (String(locale ?? '').toLowerCase().startsWith('fr') ? 'fr' : 'en')
    void import('./qa-meter').then(({ createQaMeter }) => {
      if (qaDisposed) return // shutdown() ran before the import resolved
      qaHandle = createQaMeter({
        source: qa.source,
        size: qa.size ?? 'sm',
        position: qa.position ?? defaultQaPosition(),
        locale: qaLocale,
        ...(qa.getCurrentPage && { getCurrentPage: qa.getCurrentPage }),
      })
    })
  }

  const instance: FeedbackApi & { _registerTransformer(fn: ReportTransformer): void } = {
    show() { handle.open() },
    hide() { handle.close() },
    open(opts) { handle.open(); void opts },
    async submit(partial) {
      return buildAndSubmit({
        description: partial.description,
        ...(partial.feedback_type !== undefined && { feedback_type: partial.feedback_type }),
        ...(partial.severity !== undefined && { severity: partial.severity }),
        ...(partial.synthetic !== undefined && { synthetic: partial.synthetic }),
        ...(partial.screenshot !== undefined && { screenshot: partial.screenshot }),
      })
    },
    identify(u) {
      user = u
      // Tell the mount to re-evaluate FAB visibility / Mine tab gating.
      handle.notifyIdentityChanged()
    },
    setMetadata(kv) { metadata = { ...metadata, ...kv } },
    shutdown() {
      qaDisposed = true
      qaHandle?.dispose()
      handle.dispose()
      capture.dispose()
      host.remove()
      // Only release the global if it still points at *us* — otherwise a
      // newer instance has taken ownership and we mustn't blow it away.
      const w = window as unknown as WindowWithGlobal
      if (w.mhosaicFeedback === instance) {
        delete w.mhosaicFeedback
      }
    },
    _registerTransformer(fn: ReportTransformer) { transformers.push(fn) },
  }

  // Expose the instance globally for ad-hoc callers (DevTools, docs pages,
  // help-widget integrations). The most-recently-created instance wins.
  ;(window as unknown as WindowWithGlobal).mhosaicFeedback = instance

  return instance
}
